import { cn, numberToCurrency as money } from '@/utils'
import type { HoldingRow } from '../types'
import { PageLink } from './PageLink'

const quantity = (value: number | null) =>
  value == null
    ? 'Not printed'
    : value.toLocaleString('en-US', { maximumFractionDigits: 4 })

const priceText = (value: number | null) =>
  value == null ? 'Not printed' : money(value)

const HEAD = 'pb-2.5 text-xs font-normal text-ink-subtle'
const CELL = 'py-2.5'

export interface HoldingsColumns {
  showSubAccount: boolean
  showCostBasis: boolean
}

// One column set for every group: a column shows only when every statement in view printed it.
export const holdingsColumnsFor = (
  holdings: HoldingRow[],
): HoldingsColumns => ({
  showSubAccount: holdings.some((holding) => holding.subAccount),
  showCostBasis:
    holdings.length > 0 &&
    holdings.every((holding) => holding.costBasis != null),
})

export const HoldingsTable = ({
  holdings,
  columns: { showSubAccount, showCostBasis },
}: {
  holdings: HoldingRow[]
  columns: HoldingsColumns
}) => {
  return (
    <table className="mt-4 w-full table-fixed border-collapse text-sm">
      <thead>
        <tr className="border-b text-left">
          <th className={cn(HEAD, 'w-24')}>Symbol</th>
          <th className={HEAD}>Description</th>
          {showSubAccount && <th className={cn(HEAD, 'w-28')}>Sub-account</th>}
          <th className={cn(HEAD, 'w-28 text-right')}>Quantity</th>
          <th className={cn(HEAD, 'w-28 text-right')}>Price</th>
          <th className={cn(HEAD, 'w-36 text-right')}>Value</th>
          {showCostBasis && (
            <th className={cn(HEAD, 'w-36 text-right')}>Cost basis</th>
          )}
          <th className={cn(HEAD, 'w-20')} />
        </tr>
      </thead>
      <tbody className="divide-y tabular-nums">
        {holdings.map((holding) => (
          <tr key={holding.id}>
            <td className={cn(CELL, 'truncate font-medium')}>
              {holding.symbol}
            </td>
            <td className={cn(CELL, 'truncate')} title={holding.description}>
              {holding.description}
            </td>
            {showSubAccount && (
              <td className={cn(CELL, 'truncate')}>{holding.subAccount}</td>
            )}
            <td className={cn(CELL, 'text-right')}>
              {quantity(holding.quantity)}
            </td>
            <td className={cn(CELL, 'text-right')}>
              {priceText(holding.price)}
            </td>
            <td className={cn(CELL, 'text-right')}>
              {priceText(holding.marketValue)}
            </td>
            {showCostBasis && (
              <td className={cn(CELL, 'text-right')}>
                {priceText(holding.costBasis)}
              </td>
            )}
            <td className={cn(CELL, 'text-right')}>
              <PageLink
                fileUrl={holding.statementFileUrl}
                page={holding.sourcePage}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
